import { Button, ColorPicker, Divider } from 'antd';
import { Color } from 'antd/es/color-picker';
import TextArea from 'antd/es/input/TextArea';
import cx from 'classnames';
import React, { FC, useState } from 'react';
import { TextObj } from '../../../model/GraphicModel';
import { Select } from '../../common';
import { Bold, FontColor, Italic, Underline } from '../MenuBar/Icons';
import styles from './textMenu.module.scss';

const fontSizes: number[] = [8, 9, 10, 11, 12, 14, 18, 24, 30, 36, 48, 60, 72, 96];
const recommendedColors: string[] = ['#000000', '#F5222D', '#FA8C16', '#FADB14', '#8BBB11', '#52C41A', '#13A8A8', '#1677FF', '#2F54EB', '#722ED1', '#EB2F96'];

enum Icons {
	Bold = 1,
	Italic,
	Underline,
	Dashed,
	FontColor,
}

type Props = {
	setTextObject: (redline: TextObj) => void;
};

const TextWindow: FC<Props> = ({ setTextObject }) => {
	const [text, setText] = useState<string>('');
	const [fontFamily, setFontFamily] = useState<string>('arial');
	const [fontSize, setFontSize] = useState<number>(12);
	const [fontColor, setFontColor] = useState<string>('#000');
	const [bold, setBold] = useState<boolean>(false);
	const [italic, setItalic] = useState<boolean>(false);
	const [underline, setUnderline] = useState<boolean>(false);
	const [lineThrough, setLineThrough] = useState<boolean>(false);

	const handleOnChangeColorPicker = (c: Color) => {
		const newColor: string = c.toHexString();
		setFontColor(newColor);
		setTextObject({
			bold,
			fontColor: newColor,
			fontFamily,
			fontSize,
			italic,
			lineThrough,
			text,
			underline,
		});
	};

	const handleOnChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		e.preventDefault();
		const newText: string = e.currentTarget.value;
		setText(newText);
		setTextObject({
			bold,
			fontColor,
			fontFamily,
			fontSize,
			italic,
			lineThrough,
			text: newText,
			underline,
		});
	};

	const handleOnChangeFontFamily = (ff: string) => {
		setFontFamily(ff);
		setTextObject({
			bold,
			fontColor,
			fontFamily: ff,
			fontSize,
			italic,
			lineThrough,
			text,
			underline,
		});
	};

	const handleOnChangeFontSize = (fs: number) => {
		setFontSize(fs);
		setTextObject({
			bold,
			fontColor,
			fontFamily,
			fontSize: fs,
			italic,
			lineThrough,
			text,
			underline,
		});
	};

	const toogleActiveBtn = (e: React.MouseEvent<HTMLElement>, icon: Icons) => {
		e.preventDefault();
		let isBoldActive: boolean = bold;
		let isItalicActive: boolean = italic;
		let isUnderlineActive: boolean = underline;
		let isLineThroughActive: boolean = lineThrough;
		switch (icon) {
			case Icons.Bold:
				isBoldActive = !bold;
				setBold(isBoldActive);
				break;
			case Icons.Italic:
				isItalicActive = !italic;
				setItalic(isItalicActive);
				break;
			case Icons.Underline:
				isUnderlineActive = !underline;
				setUnderline(isUnderlineActive);
				break;
			case Icons.Dashed:
				isLineThroughActive = !lineThrough;
				setLineThrough(isLineThroughActive);
				break;
		}
		setTextObject({
			bold: isBoldActive,
			fontColor,
			fontFamily,
			fontSize,
			italic: isItalicActive,
			lineThrough: isLineThroughActive,
			text,
			underline: isUnderlineActive,
		});
	};

	return (
		<div className={styles.textWindowContainer}>
			<div className={styles.formatOptions}>
				<div className={styles.selects}>
					{/* TODO: populate this select with correct data */}
					<Select
						id='fontFamily'
						className={styles.fontFamily}
						options={[
							{
								key: 'arial',
								label: 'Arial',
								value: 'arial',
							},
						]}
						onChange={handleOnChangeFontFamily}
						value={fontFamily}
					/>
					<Select
						id='fontSize'
						className={styles.fontSize}
						options={fontSizes.map(fs => {
							const strFs = fs.toString();
							return {
								title: strFs,
								label: strFs,
								key: strFs,
								id: strFs,
								value: strFs,
							};
						})}
						onChange={handleOnChangeFontSize}
						value={fontSize}
					/>
				</div>
				<div className={styles.icons}>
					<Button className={cx({ active: bold })} key={Icons.Bold} onClick={e => toogleActiveBtn(e, Icons.Bold)} htmlType='button'>
						<Bold />
					</Button>
					<Button className={cx({ active: italic })} key={Icons.Italic} onClick={e => toogleActiveBtn(e, Icons.Italic)} htmlType='button'>
						<Italic />
					</Button>
					<Button className={cx({ active: underline })} key={Icons.Underline} onClick={e => toogleActiveBtn(e, Icons.Underline)} htmlType='button'>
						<Underline />
					</Button>
					<Button className={cx({ active: lineThrough })} key={Icons.Dashed} onClick={e => toogleActiveBtn(e, Icons.Dashed)} htmlType='button'>
						{/* TODO: add LineThrough correct Icon */}
						<Underline />
					</Button>
					<ColorPicker
						disabledAlpha
						onChangeComplete={handleOnChangeColorPicker}
						panelRender={(_, { components: { Picker, Presets } }) => (
							<div
								className='custom-panel'
								style={{
									display: 'flex',
									width: 468,
								}}>
								<div
									style={{
										flex: 1,
									}}>
									<Presets />
								</div>
								<Divider
									type='vertical'
									style={{
										height: 'auto',
									}}
								/>
								<div
									style={{
										width: 234,
									}}>
									<Picker />
								</div>
							</div>
						)}
						presets={[
							{
								label: 'Recommended',
								colors: recommendedColors,
							},
						]}
						styles={{
							popupOverlayInner: {
								width: 468 + 24,
							},
						}}>
						<Button key={Icons.FontColor} htmlType='button'>
							<FontColor elementStyle={{ color: fontColor }} />
						</Button>
					</ColorPicker>
				</div>
			</div>
			<TextArea id='text' rows={4} onChange={handleOnChangeTextArea} value={text} />
		</div>
	);
};

export { TextWindow };
